import { useRef, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { BookOpen, Contact, GraduationCap, Megaphone, CreditCard, Download, FileCode2, Paperclip, Gauge, Globe2, KeyRound, LayoutDashboard, ListX, LogOut, Mail, ScrollText, Settings, Shield, Users, Webhook } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";
import { useSeo } from "../lib/seo";
import { LangToggle, Logo, Notice } from "./kit";
import { InstallBanner, IOSHelp, useInstall } from "./Install";
import { MobileTabBar, useTableLabels } from "./MobileNav";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const GROUPS = [
  [null, [["/app", "nav.dashboard", LayoutDashboard, true]]],
  ["nav.group.sending", [["/app/messages", "nav.messages", Mail], ["/app/templates", "nav.templates", FileCode2], ["/app/files", "nav.files", Paperclip], ["/app/domains", "nav.domains", Globe2], ["/app/suppressions", "nav.suppressions", ListX]]],
  ["nav.group.marketing", [["/app/campaigns", "nav.campaigns", Megaphone], ["/app/audiences", "nav.audiences", Contact]]],
  ["nav.group.dev", [["/app/api-keys", "nav.apikeys", KeyRound], ["/app/webhooks", "nav.webhooks", Webhook], ["/app/docs", "nav.docs", BookOpen], ["/guides", "nav.guides", GraduationCap]]],
  ["nav.group.workspace", [["/app/usage", "nav.usage", Gauge], ["/app/billing", "nav.billing", CreditCard], ["/app/team", "nav.team", Users], ["/app/audit", "nav.audit", ScrollText], ["/app/settings", "nav.settings", Settings]]],
];

function useGroups() {
  const { user } = useAuth();
  return user?.is_platform_admin ? [...GROUPS, ["nav.group.platform", [["/app/admin", "nav.admin", Shield]]]] : GROUPS;
}

function Nav() {
  const { t } = useT();
  const groups = useGroups();
  return (
    <nav className="space-y-7" aria-label="Main">
      {groups.map(([g, items]) => (
        <div key={g || "root"}>
          {g && <div className="mb-2 px-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-600">{t(g)}</div>}
          <div className="space-y-px">
            {items.map(([to, key, Icon, end]) => (
              <NavLink key={to} to={to} end={end} data-testid={`sidebar-${key.split(".")[1]}`}
                className={({ isActive }) => `group flex items-center gap-3 rounded-xl px-3 py-2 text-[13.5px] transition-colors ${isActive ? "bg-white/[0.06] text-white" : "text-zinc-400 hover:bg-white/[0.03] hover:text-white"}`}>
                {({ isActive }) => <><Icon className={`h-4 w-4 ${isActive ? "text-purple-300" : "text-zinc-500 group-hover:text-zinc-300"}`} />{t(key)}{isActive && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-purple-400 shadow-[0_0_10px_#c084fc]" />}</>}
              </NavLink>
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
}

function WorkspaceSwitcher({ prefix = "" }) {
  const { user, wsId, setWsId, workspace } = useAuth();
  if (!user?.workspaces?.length) return null;
  return (
    <div className="flex items-center gap-2.5">
      {workspace?.logo_url ? <img src={workspace.logo_url} alt="Workspace logo" className="h-10 w-10 shrink-0 rounded-xl border border-white/10 bg-white/[0.03] object-contain p-1" data-testid={`${prefix}sidebar-workspace-logo`} />
        : <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-white/10 bg-gradient-to-br from-purple-500/25 to-transparent font-display text-sm text-white">{(workspace?.name || "W")[0].toUpperCase()}</span>}
      <Select value={wsId || ""} onValueChange={setWsId}>
        <SelectTrigger className="h-10 min-w-0 flex-1 text-left text-sm" data-testid={`${prefix}workspace-switcher`}><SelectValue /></SelectTrigger>
        <SelectContent>
          {user.workspaces.map((w) => <SelectItem key={w.id} value={w.id} data-testid={`${prefix}workspace-option-${w.id}`}>{w.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function VerifyBanner() {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!user || user.email_verified) return null;
  const resend = async () => {
    setBusy(true);
    try { await api.post("/auth/resend-verification"); toast.success("Verification link issued"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mb-6"><Notice tone="warn" title="Verify your email address" testId="verify-email-banner">
      You need a verified email to create API keys. <button className="underline" onClick={resend} disabled={busy} data-testid="resend-verification">Resend link</button>
    </Notice></div>
  );
}

function SidebarFooter({ onSignOut }) {
  const { t } = useT();
  const { user, workspace } = useAuth();
  const { can, install, help, setHelp } = useInstall();
  return (
    <div className="space-y-3 border-t border-white/[0.06] pt-4">
      {can && <button onClick={install} className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-xs text-purple-200 hover:bg-purple-500/10" data-testid="sidebar-install-app"><Download className="h-3.5 w-3.5" />{t("pwa.title")}</button>}
      <div className="px-2 text-xs text-zinc-500"><div className="truncate text-zinc-300" data-testid="current-user-email">{user?.email}</div>{workspace && <span className="capitalize">{t(`role.${workspace.role}`)}</span>}</div>
      <div className="flex items-center justify-between px-1">
        <LangToggle />
        <Button variant="ghost" size="sm" onClick={onSignOut} className="text-zinc-400 hover:bg-white/5 hover:text-white" data-testid="signout-button"><LogOut className="h-4 w-4" />{t("nav.signout")}</Button>
      </div>
      <IOSHelp open={help} onOpenChange={setHelp} />
    </div>
  );
}

export default function AppShell() {
  const { logout, wsId } = useAuth();
  useSeo({ title: "Dashboard", description: "Your CARIO Mail workspace.", path: "/app", noindex: true });
  const nav = useNavigate();
  const groups = useGroups();
  const mainRef = useRef(null);
  useTableLabels(mainRef);
  const signOut = async () => { await logout(); nav("/login"); };
  return (
    <div className="grain halo min-h-screen bg-[#050507]">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-[264px] border-r border-white/[0.06] bg-[#050507]/90 backdrop-blur-xl lg:block">
        <div className="flex h-full flex-col gap-7 p-5">
          <Link to="/" className="px-2 pt-1"><Logo testId="sidebar-logo" /></Link>
          <WorkspaceSwitcher />
          <div className="scroll-thin -mx-1 flex-1 overflow-y-auto px-1"><Nav /></div>
          <SidebarFooter onSignOut={signOut} />
        </div>
      </aside>
      <div className="lg:pl-[264px]">
        <header className="pt-safe sticky top-0 z-30 border-b border-white/[0.06] bg-[#050507]/80 backdrop-blur-2xl lg:hidden">
          <div className="flex h-14 items-center justify-between px-4"><Link to="/app" data-testid="mobile-logo-link"><Logo testId="mobile-logo" /></Link><LangToggle prefix="mobile-" /></div>
        </header>
        <main ref={mainRef} className="app-main tabbar-space relative z-10 mx-auto max-w-6xl px-4 py-7 sm:px-6 lg:px-12 lg:py-14">
          <VerifyBanner />
          <Outlet key={wsId} />
        </main>
      </div>
      <MobileTabBar groups={groups} workspaceSwitcher={<WorkspaceSwitcher prefix="mobile-" />} onSignOut={signOut} />
      <InstallBanner />
    </div>
  );
}
